import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthSessionStore } from '@indutrust/auth-data';
import type { CompanyFactorySummary } from '@indutrust/factory-model';
import {
  resolveHttpError,
  withCrudEntitiesEvents,
  withLoadAllOnAuthEvents,
  withOrderedEntities,
} from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { CompanyFactoryApiClient } from '../company-factory-api-client.service';
import { companyFactoriesCrudEvents } from './company-factories.events';

/**
 * Signal store managing factories that belong to the authenticated company.
 *
 * Maintains a collection of {@link CompanyFactorySummary} entities — the
 * lightweight projection returned by `GET /companies/{companyId}/factories`
 * — with event-driven CRUD operations, ordering helpers, and automatic
 * loading on authentication.
 *
 * Provides custom event handling for company-specific factory loading and creation with navigation.
 *
 * @example
 * const store = inject(CompanyFactoriesStore);
 * store.loadAll();
 */
export const CompanyFactoriesStore = signalStore(
  { providedIn: 'root' },
  // Don't pass serviceFactory to prevent automatic API handling for loadAll
  // We need custom handling to pass the company ID from auth context
  withCrudEntitiesEvents<CompanyFactorySummary>(companyFactoriesCrudEvents),
  withLoadAllOnAuthEvents(companyFactoriesCrudEvents),
  withOrderedEntities<CompanyFactorySummary>(),
  // Custom event handlers for company-specific logic
  withEventHandlers(
    (
      _,
      events = inject(Events),
      companyFactoryService = inject(CompanyFactoryApiClient),
      authStore = inject(AuthSessionStore),
      router = inject(Router)
    ) => ({
      /**
       * Handles loadAll events for company factories.
       * Passes the authenticated user's company ID to the API.
       */
      loadCompanyFactories$: events
        .on(companyFactoriesCrudEvents.loadAllRequested)
        .pipe(
          switchMap(({ payload: { params } }) => {
            return companyFactoryService
              .getAll(
                { companyId: String(authStore.user()?.company_id) },
                params
              )
              .pipe(
                mapResponse({
                  next: (factories) =>
                    companyFactoriesCrudEvents.loadAllSucceeded(factories),
                  error: (error: HttpErrorResponse) =>
                    companyFactoriesCrudEvents.loadAllFailed(
                      resolveHttpError(error).code
                    ),
                })
              );
          })
        ),
      /**
       * Handles `addRequested` events for factory creation.
       *
       * `withCrudEntitiesEvents` is intentionally instantiated without a
       * `serviceFactory` (see comment above), so the default add handler
       * is not registered — the create endpoint is custom
       * (`POST /factories` rather than the company-scoped collection
       * URL `/companies/{companyId}/factories`). We perform the call
       * here using {@link CompanyFactoryApiClient.createFactory}
       * which only needs the factory name.
       */
      createFactory$: events.on(companyFactoriesCrudEvents.addRequested).pipe(
        switchMap(({ payload: { payload } }) => {
          const name = payload.name ?? '';
          return companyFactoryService.createFactory(name).pipe(
            mapResponse({
              next: (factory) =>
                companyFactoriesCrudEvents.addSucceeded(factory),
              error: (error: HttpErrorResponse) =>
                companyFactoriesCrudEvents.addFailed(
                  resolveHttpError(error).code
                ),
            })
          );
        })
      ),
      /**
       * Reacts to a successful factory creation by sending the supplier
       * straight into the editor at `/factory/:id/edit` — the next step
       * of the supplier-onboarding journey
       * (`docs/product/journeys/supplier-onboarding-first-factory.md`).
       *
       * Listens to `addSucceeded` (not `addRequested`) so navigation
       * only fires once the API has confirmed the factory exists; a
       * failed POST emits `addFailed` instead and leaves the user on
       * the company hub with the error visible.
       */
      addFactoryAndNavigate$: events
        .on(companyFactoriesCrudEvents.addSucceeded)
        .pipe(
          switchMap(({ payload: factory }) => {
            router.navigate(['factory', factory.id, 'edit']).catch(() => {
              // Navigation failed, but the factory was created successfully
            });
            return [];
          })
        ),
    })
  )
);
